import React, { useContext, useEffect, useRef, useState } from "react";
import {
    Modal,
    ModalHeader,
    ModalBody,
    Form,
    FormGroup,
    Label,
    Input,
    Row,
    Col,
    Button,
    Spinner,
} from "reactstrap";
import InputMask from "react-input-mask";
import Swal from "sweetalert2";

import useUser from "../../context/hooks/useUser";

import { AuthContext } from "../../context/AuthContext";
import { AlunoContext } from "../../context/AlunoContext";
import { CursoContext } from "../../context/CursoContext";
import { CreateAlunoType } from "../../context/hooks/useAluno";
import { CursoType } from "../../context/hooks/useCurso";

interface Props {
    isOpen: boolean;
    toggle: () => void;
    contratoId: number;
    onAlunoCriado?: () => void;
}

type ViaCepResponse = {
    cep?: string;
    logradouro?: string;
    complemento?: string;
    bairro?: string;
    localidade?: string;
    uf?: string;
    erro?: boolean;
};

export const CreateAlunoModal: React.FC<Props> = ({
    isOpen,
    toggle,
    contratoId,
    onAlunoCriado,
}) => {
    const { objUserTk } = useContext(AuthContext);
    const { validateCpf } = useUser();
    const { createAluno, validateReferenceCode } = useContext(AlunoContext);
    const { handleGetCursoByContratoId, listCursoByContrato } =
        useContext(CursoContext);

    const defaultAluno = (): CreateAlunoType => ({
        referenceCode: 0,
        name: "",
        email: "",
        cpf: "",
        address: "",
        addressNumber: "",
        neighborhood: "",
        city: "",
        state: "",
        zipCode: "",
        landline: "",
        telephone_01: "",
        telephone_02: "",
        telephone_03: "",
        telephone_04: "",
        telephone_05: "",
        telephone_06: "",
        facebook: "",
        instagram: "",
        keepThePhotos: false,
        hasAlbum: false,
        numberOfPhotos: 0,
        password: "",
        contratoId,
        cursoId: 0,
        base64Image: "",
        observacao: "",
    });

    const clearFieldsKeepCourse = (currentCursoId: number) => {
        setAluno({
            ...defaultAluno(),
            cursoId: currentCursoId,
        });
        setProfileImage(null);

        // ViaCEP state
        setZipLoading(false);
        setZipError(null);
        lastZipFetchedRef.current = "";
        if (zipAbortRef.current) {
            zipAbortRef.current.abort();
            zipAbortRef.current = null;
        }
        if (zipDebounceRef.current) {
            window.clearTimeout(zipDebounceRef.current);
            zipDebounceRef.current = null;
        }
    };

    const [aluno, setAluno] = useState<CreateAlunoType>(defaultAluno());
    const [profileImage, setProfileImage] = useState<File | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // ViaCEP
    const [zipLoading, setZipLoading] = useState(false);
    const [zipError, setZipError] = useState<string | null>(null);
    const lastZipFetchedRef = useRef<string>("");
    const zipAbortRef = useRef<AbortController | null>(null);
    const zipDebounceRef = useRef<number | null>(null);

    const onlyDigits = (s?: string) => (s ?? "").replace(/\D/g, "");

    useEffect(() => {
        if (isOpen && contratoId) {
            handleGetCursoByContratoId(contratoId);
            setAluno(defaultAluno());

            setZipLoading(false);
            setZipError(null);
            lastZipFetchedRef.current = "";
            if (zipAbortRef.current) {
                zipAbortRef.current.abort();
                zipAbortRef.current = null;
            }
            if (zipDebounceRef.current) {
                window.clearTimeout(zipDebounceRef.current);
                zipDebounceRef.current = null;
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, contratoId]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value, type, checked } = e.target;

        if (name === "zipCode") setZipError(null);

        setAluno((prev) => ({
            ...prev,
            [name]: type === "checkbox" ? checked : value,
        }));
    };

    const handleImage = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setProfileImage(file);

        const reader = new FileReader();
        reader.onloadend = () => {
            setAluno((prev) => ({ ...prev, base64Image: reader.result as string }));
        };
        reader.readAsDataURL(file);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (isSubmitting) return;
        setIsSubmitting(true);
        const created = await createAluno({ ...aluno, contratoId });
        setIsSubmitting(false);
        if (created) {
            onAlunoCriado?.();
            clearFieldsKeepCourse(aluno.cursoId);
        }
    };

    const handleValidateCpf = async () => {
        const rawCpf = aluno.cpf?.replace(/\D/g, "") ?? "";
        if (!rawCpf || rawCpf.length !== 11) return;

        const result = await validateCpf(rawCpf);

        if (result?.tipo) {
            const confirm = await Swal.fire({
                icon: "warning",
                title: "CPF já cadastrado!",
                html: `<b>Tipo:</b> ${result.tipo}<br/><b>Nome:</b> ${result.nome}<br/><br/>Deseja continuar mesmo assim?`,
                showCancelButton: true,
                confirmButtonText: "Continuar preenchimento",
                cancelButtonText: "Cancelar e limpar dados",
                reverseButtons: true,
            });

            if (!confirm.isConfirmed) {
                clearFieldsKeepCourse(aluno.cursoId);
            }
        }
    };

    const handleValidateReferenceCode = async () => {
        const refCode = Number(aluno.referenceCode);
        if (!refCode || isNaN(refCode)) return;

        const result = await validateReferenceCode(refCode);

        if (result?.nome) {
            await Swal.fire({
                icon: "warning",
                title: "Código de referência já utilizado!",
                html: `<b>Aluno:</b> ${result.nome}<br/><br/>O campo será limpo para que você informe um código válido.`,
                confirmButtonText: "Entendido",
            });

            setAluno((prev) => ({ ...prev, referenceCode: 0 }));
        }
    };

    const applyViaCepToAluno = (data: ViaCepResponse) => {
        // Não sobrescreve se usuário já digitou manualmente
        setAluno((prev) => ({
            ...prev,
            state: prev.state?.trim() ? prev.state : data.uf || "",
            city: prev.city?.trim() ? prev.city : data.localidade || "",
            neighborhood: prev.neighborhood?.trim()
                ? prev.neighborhood
                : data.bairro || "",
            address: prev.address?.trim() ? prev.address : data.logradouro || "",
        }));
    };

    const fetchViaCep = async (zipDigits: string) => {
        if (zipDigits.length !== 8) return;
        if (lastZipFetchedRef.current === zipDigits) return;

        if (zipAbortRef.current) zipAbortRef.current.abort();
        const controller = new AbortController();
        zipAbortRef.current = controller;

        setZipLoading(true);
        setZipError(null);

        try {
            const resp = await fetch(`https://viacep.com.br/ws/${zipDigits}/json/`, {
                method: "GET",
                signal: controller.signal,
            });

            if (!resp.ok) {
                throw new Error(`Falha ao consultar CEP (HTTP ${resp.status})`);
            }

            const json = (await resp.json()) as ViaCepResponse;

            if (json.erro) {
                setZipError("CEP não encontrado. Verifique e tente novamente.");
                return;
            }

            lastZipFetchedRef.current = zipDigits;
            applyViaCepToAluno(json);
        } catch (err: any) {
            if (err?.name === "AbortError") return;
            setZipError("Não foi possível consultar o CEP agora. Tente novamente.");
        } finally {
            setZipLoading(false);
        }
    };

    // Auto-busca quando completar 8 dígitos do CEP
    useEffect(() => {
        const zipDigits = onlyDigits(aluno.zipCode);

        if (zipDigits.length !== 8) {
            setZipLoading(false);
            return;
        }

        if (zipDebounceRef.current) window.clearTimeout(zipDebounceRef.current);

        zipDebounceRef.current = window.setTimeout(() => {
            fetchViaCep(zipDigits);
        }, 350);

        return () => {
            if (zipDebounceRef.current) {
                window.clearTimeout(zipDebounceRef.current);
                zipDebounceRef.current = null;
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [aluno.zipCode]);

    return (
        <Modal isOpen={isOpen} toggle={toggle} backdrop="static" size="xl">
            <ModalHeader toggle={toggle}>Novo Aluno</ModalHeader>
            <ModalBody>
                <Form onSubmit={handleSubmit}>
                    <Row>
                        <Col md="6">
                            <FormGroup>
                                <Label className="form-control-label">Foto de Perfil</Label>
                                <Input
                                    className="form-control-alternative"
                                    type="file"
                                    accept="image/*"
                                    onChange={handleImage}
                                />
                            </FormGroup>
                        </Col>
                        <Col md="6">
                            <FormGroup>
                                <Label className="form-control-label">Código de Referência</Label>
                                <Input
                                    className="form-control-alternative"
                                    type="number"
                                    name="referenceCode"
                                    value={aluno.referenceCode}
                                    placeholder="Código de referência"
                                    onChange={handleChange}
                                    onBlur={handleValidateReferenceCode}
                                    required
                                />
                            </FormGroup>
                        </Col>
                    </Row>

                    <Row>
                        <Col md="6">
                            <FormGroup>
                                <Label className="form-control-label">Nome</Label>
                                <Input
                                    className="form-control-alternative"
                                    name="name"
                                    value={aluno.name}
                                    placeholder="Digite o nome"
                                    onChange={handleChange}
                                    required
                                />
                            </FormGroup>
                        </Col>
                        <Col md="6">
                            <FormGroup>
                                <Label className="form-control-label">Email</Label>
                                <Input
                                    className="form-control-alternative"
                                    type="email"
                                    name="email"
                                    value={aluno.email}
                                    placeholder="Digite o email"
                                    onChange={handleChange}
                                />
                            </FormGroup>
                        </Col>
                    </Row>

                    <Row>
                        <Col md="6">
                            <FormGroup>
                                <Label className="form-control-label">CPF</Label>
                                <InputMask
                                    className="form-control form-control-alternative"
                                    mask="999.999.999-99"
                                    name="cpf"
                                    value={aluno.cpf ?? ""}
                                    placeholder="000.000.000-00"
                                    onChange={handleChange}
                                    onBlur={handleValidateCpf}
                                />
                            </FormGroup>
                        </Col>
                        <Col md="6">
                            <FormGroup>
                                <Label className="form-control-label">Curso</Label>
                                <Input
                                    className="form-control-alternative"
                                    type="select"
                                    name="cursoId"
                                    value={aluno.cursoId}
                                    onChange={handleChange}
                                    required
                                >
                                    <option value="0" disabled>
                                        Selecione um curso
                                    </option>
                                    {listCursoByContrato.length ? (
                                        listCursoByContrato.map((c: CursoType) => (
                                            <option key={c.id} value={c.id}>
                                                {c.name}
                                            </option>
                                        ))
                                    ) : (
                                        <option value="0" disabled>
                                            Nenhum curso disponível
                                        </option>
                                    )}
                                </Input>
                            </FormGroup>
                        </Col>
                    </Row>

                    <Row>
                        <Col md="4">
                            <FormGroup>
                                <Label className="form-control-label" for="Address">
                                    Endereço
                                </Label>
                                <Input
                                    className="form-control-alternative"
                                    type="text"
                                    name="address"
                                    id="address"
                                    value={aluno.address}
                                    placeholder="Endereço"
                                    onChange={handleChange}
                                />
                            </FormGroup>
                        </Col>
                        <Col md="4">
                            <FormGroup>
                                <Label className="form-control-label" for="addressNumber">
                                    Número
                                </Label>
                                <Input
                                    className="form-control-alternative"
                                    type="text"
                                    name="addressNumber"
                                    id="addressNumber"
                                    value={aluno.addressNumber}
                                    placeholder="Número"
                                    onChange={handleChange}
                                />
                            </FormGroup>
                        </Col>
                        <Col md="4">
                            <FormGroup>
                                <Label className="form-control-label" for="neighborhood">
                                    Bairro
                                </Label>
                                <Input
                                    className="form-control-alternative"
                                    type="text"
                                    name="neighborhood"
                                    id="neighborhood"
                                    value={aluno.neighborhood}
                                    placeholder="Bairro"
                                    onChange={handleChange}
                                />
                            </FormGroup>
                        </Col>
                    </Row>

                    <Row>
                        <Col md="4">
                            <FormGroup>
                                <Label
                                    className="form-control-label d-flex align-items-center justify-content-between"
                                    for="zipCode"
                                >
                                    <span>CEP</span>
                                    {zipLoading && (
                                        <span className="d-inline-flex align-items-center">
                                            <Spinner size="sm" />
                                            <span className="ml-2" style={{ fontSize: 12, opacity: 0.8 }}>
                                                Buscando...
                                            </span>
                                        </span>
                                    )}
                                </Label>

                                <InputMask
                                    className="form-control form-control-alternative"
                                    mask="99999-999"
                                    type="text"
                                    name="zipCode"
                                    id="zipCode"
                                    value={aluno.zipCode ?? ""}
                                    placeholder="00000-000"
                                    onChange={handleChange}
                                />

                                {zipError && (
                                    <div style={{ color: "#d9534f", fontSize: 12, marginTop: 6 }}>
                                        {zipError}
                                    </div>
                                )}
                            </FormGroup>
                        </Col>

                        <Col md="4">
                            <FormGroup>
                                <Label className="form-control-label" for="state">
                                    Estado
                                </Label>
                                <Input
                                    className="form-control-alternative"
                                    type="text"
                                    name="state"
                                    id="state"
                                    value={aluno.state}
                                    placeholder="Estado"
                                    onChange={handleChange}
                                />
                            </FormGroup>
                        </Col>

                        <Col md="4">
                            <FormGroup>
                                <Label className="form-control-label" for="city">
                                    Cidade
                                </Label>
                                <Input
                                    className="form-control-alternative"
                                    type="text"
                                    name="city"
                                    id="city"
                                    value={aluno.city}
                                    placeholder="Cidade"
                                    onChange={handleChange}
                                />
                            </FormGroup>
                        </Col>
                    </Row>

                    <Row>
                        <Col md="4">
                            <FormGroup>
                                <Label className="form-control-label" for="landline">
                                    Telefone Fixo
                                </Label>
                                <InputMask
                                    className="form-control form-control-alternative"
                                    mask="(99) 9999-9999"
                                    type="text"
                                    name="landline"
                                    id="landline"
                                    value={aluno.landline ?? ""}
                                    placeholder="(00) 0000-0000"
                                    onChange={handleChange}
                                />
                            </FormGroup>
                        </Col>
                        <Col md="4">
                            <FormGroup>
                                <Label className="form-control-label" for="telephone_01">
                                    Telefone 01
                                </Label>
                                <InputMask
                                    className="form-control form-control-alternative"
                                    mask="(99) 99999-9999"
                                    type="text"
                                    name="telephone_01"
                                    id="telephone_01"
                                    value={aluno.telephone_01 ?? ""}
                                    placeholder="(00) 00000-0000"
                                    onChange={handleChange}
                                />
                            </FormGroup>
                        </Col>
                        <Col md="4">
                            <FormGroup>
                                <Label className="form-control-label" for="telephone_02">
                                    Telefone 02
                                </Label>
                                <InputMask
                                    className="form-control form-control-alternative"
                                    mask="(99) 99999-9999"
                                    type="text"
                                    name="telephone_02"
                                    id="telephone_02"
                                    value={aluno.telephone_02 ?? ""}
                                    placeholder="(00) 00000-0000"
                                    onChange={handleChange}
                                />
                            </FormGroup>
                        </Col>
                    </Row>

                    <Row>
                        <Col md="6">
                            <FormGroup>
                                <Label className="form-control-label" for="telephone_03">
                                    Telefone 03
                                </Label>
                                <InputMask
                                    className="form-control form-control-alternative"
                                    mask="(99) 99999-9999"
                                    type="text"
                                    name="telephone_03"
                                    id="telephone_03"
                                    value={aluno.telephone_03 ?? ""}
                                    placeholder="(00) 00000-0000"
                                    onChange={handleChange}
                                />
                            </FormGroup>
                        </Col>
                        <Col md="6">
                            <FormGroup>
                                <Label className="form-control-label" for="telephone_04">
                                    Telefone 04
                                </Label>
                                <InputMask
                                    className="form-control form-control-alternative"
                                    mask="(99) 99999-9999"
                                    type="text"
                                    name="telephone_04"
                                    id="telephone_04"
                                    value={aluno.telephone_04 ?? ""}
                                    placeholder="(00) 00000-0000"
                                    onChange={handleChange}
                                />
                            </FormGroup>
                        </Col>
                    </Row>

                    <Row>
                        <Col md="6">
                            <FormGroup>
                                <Label className="form-control-label" for="telephone_05">
                                    Telefone 05
                                </Label>
                                <InputMask
                                    className="form-control form-control-alternative"
                                    mask="(99) 99999-9999"
                                    type="text"
                                    name="telephone_05"
                                    id="telephone_05"
                                    value={aluno.telephone_05 ?? ""}
                                    placeholder="(00) 00000-0000"
                                    onChange={handleChange}
                                />
                            </FormGroup>
                        </Col>
                        <Col md="6">
                            <FormGroup>
                                <Label className="form-control-label" for="telephone_06">
                                    Telefone 06
                                </Label>
                                <InputMask
                                    className="form-control form-control-alternative"
                                    mask="(99) 99999-9999"
                                    type="text"
                                    name="telephone_06"
                                    id="telephone_06"
                                    value={aluno.telephone_06 ?? ""}
                                    placeholder="(00) 00000-0000"
                                    onChange={handleChange}
                                />
                            </FormGroup>
                        </Col>
                    </Row>

                    <Row>
                        <Col md="6">
                            <FormGroup>
                                <Label className="form-control-label" for="facebook">
                                    Facebook
                                </Label>
                                <Input
                                    className="form-control-alternative"
                                    type="text"
                                    name="facebook"
                                    id="facebook"
                                    value={aluno.facebook}
                                    placeholder="Facebook"
                                    onChange={handleChange}
                                />
                            </FormGroup>
                        </Col>
                        <Col md="6">
                            <FormGroup>
                                <Label className="form-control-label" for="instagram">
                                    Instagram
                                </Label>
                                <Input
                                    className="form-control-alternative"
                                    type="text"
                                    name="instagram"
                                    id="instagram"
                                    value={aluno.instagram}
                                    placeholder="Instagram"
                                    onChange={handleChange}
                                />
                            </FormGroup>
                        </Col>
                    </Row>

                    <Row>
                        <Col md="12">
                            <FormGroup>
                                <Label className="form-control-label" for="observacao">
                                    Observação
                                </Label>
                                <Input
                                    className="form-control-alternative"
                                    type="textarea"
                                    rows={3}
                                    name="observacao"
                                    id="observacao"
                                    value={aluno.observacao ?? ""}
                                    placeholder="Observações sobre o aluno"
                                    onChange={(e) =>
                                        setAluno((prev) => ({ ...prev, observacao: e.target.value }))
                                    }
                                />
                            </FormGroup>
                        </Col>
                    </Row>

                    <div className="d-flex justify-content-end mt-4">
                        <Button style={{ background: "#585858", color: "#fff" }} type="submit" disabled={isSubmitting}>
                            Criar aluno
                        </Button>
                        <Button
                            style={{ background: "#d9534f", color: "#fff" }}
                            className="ml-2"
                            onClick={toggle}
                            type="button"
                        >
                            Cancelar
                        </Button>
                    </div>
                </Form>
            </ModalBody>
        </Modal>
    );
};

export default CreateAlunoModal;